import type { components } from "@cntrl/api-client";

type LineupSlotDto = components["schemas"]["LineupSlotDto"];
type ScheduleItemDto = components["schemas"]["ScheduleItemDto"];

/**
 * "Which act on this bill is US?" — and, from that, "which run-of-show row is OUR set?"
 *
 * Both questions used to be answered by `items.find(i => i.section === 'stage')`, which was right only while
 * exactly one row carried that section. The 2026-08-23 structured rebuild put four rows under `stage` (two
 * changeovers, the support set, the headliner set), so `find` started returning the first — an untimed
 * changeover on the mobile set clock, a changeover time in the dashboard's "Showtime" tile, and the SUPPORT
 * act's set in the day-of-show reminder. Attribution (`ScheduleItemDto.slotId`) fixed the storage; this is
 * the read side of that fix, in one place so the three call sites cannot drift again.
 */

/**
 * The lineup slot the show's own band occupies, identified by `friendBandId` pointing back at the owner.
 *
 * Never by NAME: matching an act to a band by string is legacy's `friendByName`, which `ShowLineupSlot.cs`
 * explicitly refuses to port — it re-derives identity from a display value and silently attaches to the wrong
 * band on a rename or a near-match. Shows created from 2026-08-25 carry this slot from birth and the backfill
 * repaired the rest, so a null here means genuinely unknown rather than "not looked up yet".
 */
export function ownSlotId(
  lineup: readonly LineupSlotDto[] | null | undefined,
  ownerBandId: string | null | undefined,
): string | null {
  if (!ownerBandId) return null;
  return lineup?.find((s) => s.friendBandId === ownerBandId)?.slotId ?? null;
}

/**
 * The band's own on-stage set.
 *
 * Falls back to an UNATTRIBUTED `stage` row — a pre-attribution row the backfill could not place — but never
 * to another act's row. That asymmetry is the whole point: showing nothing is a visibly empty card, while
 * showing the support act's 8pm as your stage time is wrong in a way nobody catches until they miss it.
 */
export function ownStageRow(
  items: readonly ScheduleItemDto[] | null | undefined,
  slotId: string | null,
): ScheduleItemDto | null {
  const stage = (items ?? []).filter((i) => i.section === "stage");
  if (slotId) {
    const mine = stage.find((i) => i.slotId === slotId);
    if (mine) return mine;
  }
  return stage.find((i) => i.slotId == null) ?? null;
}
